/* globals CSS_CONFIG, SUB_PATH */
import { type MiddlewareFunction } from 'react-router';
import analyticsApiClient from '../../services/analytics';
import { sendEventToOptimizely } from '../../optimizely';
import { getBokaSubPathname } from '../../helpers/urlHelper';
import store from '../../store';
import { viewContext } from './context';

let previousPathname: string | null = null;

const analyticsMiddleware: MiddlewareFunction = async (
  { request, context },
  next,
) => {
  await next();

  const view = context.get(viewContext);

  const url = new URL(request.url);
  const { pathname } = url;

  const { sendEventToEpisod, sendPageview } = analyticsApiClient();
  const { app, search, services, stores } = store.getState();
  const { operation, booking } = app;
  const { serviceId, storeId, replacementService } = app.selected;

  if (pathname.endsWith('/services/')) {
    const eventName = `begin_${operation}_appointment`;
    sendEventToEpisod(eventName, {
      postalCode: search.postCode,
      step: '1',
    });
    sendEventToOptimizely(eventName);
  }

  if (pathname.endsWith('/timeslots/')) {
    const eventName = `${serviceId}_${operation}_appointment`;
    sendEventToEpisod(eventName, {
      postalCode: search.postCode,
      step: '2',
      options: {
        serviceId,
        services: services.availableServices,
      },
    });
    sendEventToOptimizely(eventName);
  }

  if (pathname.endsWith('/contact/') && operation && serviceId) {
    if (replacementService.timeSlot) {
      const replacementServiceId = services.availableServices.find(
        (service) => service.serviceId === serviceId,
      )?.replacementService?.serviceId;
      const eventName = `select_replacement_date_and_time_${replacementServiceId}_${operation}_appointment`;
      sendEventToEpisod(eventName, {
        postalCode: search.postCode,
        step: '3',
        options: {
          serviceId,
          replacementServiceId,
          services: services.availableServices,
          storeId: replacementService.storeId,
          stores: stores.replacementServiceStores,
        },
      });
      sendEventToOptimizely(eventName);
    } else {
      const eventName = `select_date_and_time_${serviceId}_${operation}_appointment`;
      sendEventToEpisod(eventName, {
        postalCode: search.postCode,
        step: '3',
        options: {
          serviceId,
          services: services.availableServices,
          storeId,
          stores: stores.availableStores,
        },
      });
      sendEventToOptimizely(eventName);
    }
  }

  if (pathname.endsWith('/confirm/')) {
    if (CSS_CONFIG.optimizelyEvents.completeBooking) {
      sendEventToOptimizely('booking_complete_global');
      sendEventToOptimizely(`booking_complete_${CSS_CONFIG.country}`);
    }
    if (replacementService.timeSlot) {
      const replacementServiceId = services.availableServices.find(
        (service) => service.serviceId === serviceId,
      )?.replacementService?.serviceId;
      const eventName = `complete_replacement_${replacementServiceId}_${operation}_appointment`;
      sendEventToEpisod(eventName, {
        postalCode: search.postCode,
        step: '4',
        options: {
          serviceId,
          replacementServiceId,
          services: services.availableServices,
          storeId: replacementService.storeId,
          stores: stores.replacementServiceStores,
          orderNumber: booking.orderNo,
        },
      });
      sendEventToOptimizely(eventName);
    } else {
      const eventName = `complete_${serviceId}_${operation}_appointment`;
      sendEventToEpisod(eventName, {
        postalCode: search.postCode,
        step: '4',
        options: {
          serviceId,
          services: services.availableServices,
          storeId,
          stores: stores.availableStores,
          orderNumber: booking.orderNo,
        },
      });
      sendEventToOptimizely(eventName);
    }
  }

  const currentPath = getBokaSubPathname(pathname, SUB_PATH, view);
  const prevPath = previousPathname
    ? getBokaSubPathname(previousPathname, SUB_PATH, view)
    : null;

  if (prevPath !== null && currentPath !== prevPath) {
    sendPageview();
  }

  previousPathname = pathname;
};

export default analyticsMiddleware;
